/**
 * Crop-and-upload an avatar image. The user drags an image into the
 * frame, then pans / zooms a circular crop window, then saves. The
 * cropped 256×256 image is exported as JPEG and sent to /api/me/avatar.
 *
 * Why client-side rather than server-side resize? Workers have no
 * native image API and we don't want a Sharp dependency in the worker.
 * Browsers can resize trivially via canvas.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Modal } from './Modal';
import { Spinner } from './Spinner';
import { UiNode } from '../lib/UiNode';

const FRAME = 320;
const OUTPUT_SIZE = 256;
const OUTPUT_QUALITY = 0.9;

export function AvatarEditor({
  initialUrl,
  onClose,
  onSaved,
}: {
  initialUrl: string | null;
  onClose: () => void;
  onSaved: (url: string | null) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const draggingRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(
    null,
  );
  // Track the currently-active blob URL so we can revoke it when the
  // user picks a different image or closes the editor — NOT inside
  // i.onload like we used to. Revoking before React renders made every
  // <img src={img.src}> in the preview thumbs try to load a dead URL
  // (broken-image icon).
  const blobUrlRef = useRef<string | null>(null);
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  function loadFile(file: File) {
    if (!file.type.startsWith('image/')) {
      setError('That file isn\'t an image.');
      return;
    }
    setError(null);
    // Replace any in-flight blob URL — only the latest pick is live.
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    const url = URL.createObjectURL(file);
    blobUrlRef.current = url;
    const i = new Image();
    i.onload = () => {
      // Default zoom = cover the frame (so the crop window starts full).
      const cover = Math.max(FRAME / i.naturalWidth, FRAME / i.naturalHeight);
      setZoom(cover);
      setOffset({ x: 0, y: 0 });
      setImg(i);
    };
    i.onerror = () => {
      if (blobUrlRef.current === url) {
        URL.revokeObjectURL(url);
        blobUrlRef.current = null;
      }
      setError("That file couldn't be decoded.");
    };
    i.src = url;
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) loadFile(f);
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!img) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    draggingRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      ox: offset.x,
      oy: offset.y,
    };
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const d = draggingRef.current;
    if (!d || !img) return;
    setOffset({
      x: d.ox + (e.clientX - d.startX),
      y: d.oy + (e.clientY - d.startY),
    });
  }
  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    draggingRef.current = null;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  }

  function reset() {
    if (!img) return;
    const cover = Math.max(FRAME / img.naturalWidth, FRAME / img.naturalHeight);
    setZoom(cover);
    setOffset({ x: 0, y: 0 });
  }

  async function save() {
    if (!img) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await renderCrop(img, zoom, offset);
      const buf = await blob.arrayBuffer();
      const r = await fetch('/api/me/avatar', {
        method: 'POST',
        headers: { 'content-type': 'image/jpeg' },
        body: buf,
      });
      if (!r.ok) {
        throw new Error((await r.text()) || `Upload failed (${r.status})`);
      }
      const { avatarUrl } = (await r.json()) as { avatarUrl: string };
      onSaved(avatarUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm('Remove your avatar?')) return;
    setBusy(true);
    try {
      await api.deleteAvatar();
      onSaved(null);
    } finally {
      setBusy(false);
    }
  }

  // Keep the image inside reasonable bounds so panning doesn't fly off.
  useEffect(() => {
    if (!img) return;
    const maxX = (img.naturalWidth * zoom - FRAME) / 2;
    const maxY = (img.naturalHeight * zoom - FRAME) / 2;
    setOffset((cur) => ({
      x: Math.max(-Math.max(maxX, 0), Math.min(Math.max(maxX, 0), cur.x)),
      y: Math.max(-Math.max(maxY, 0), Math.min(Math.max(maxY, 0), cur.y)),
    }));
  }, [zoom, img]);

  return (
    <Modal open onClose={onClose} title="Profile photo" maxWidth={460}>
      <div className="flex flex-col items-center gap-3">
        <div
          className="relative overflow-hidden rounded-2xl border border-dashed border-ink-rim bg-ink-3"
          style={{ width: FRAME, height: FRAME }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {img ? (
            <>
              <img
                draggable={false}
                src={img.src}
                alt=""
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: img.naturalWidth * zoom,
                  height: img.naturalHeight * zoom,
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                  userSelect: 'none',
                  cursor: 'grab',
                }}
              />
              {/* Circular crop mask */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  boxShadow: `0 0 0 1000px rgba(8, 6, 12, 0.55)`,
                  borderRadius: '50%',
                  margin: 12,
                }}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-3 rounded-full border border-cream/30"
              />
            </>
          ) : (
            <button
              type="button"
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-sm text-cream-3 transition hover:bg-ink-4 hover:text-cream-2"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Choose an image to upload"
            >
              <span
                className="inline-flex h-12 w-12 items-center justify-center rounded-full"
                style={{
                  background: 'color-mix(in oklab, var(--color-clove) 22%, transparent)',
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </span>
              <span className="text-base font-medium text-cream">Pick an image</span>
              <span className="text-xs">or drag one onto this square</span>
              <span className="mt-1 text-[11px]">PNG / JPEG / WebP / GIF up to 2 MB</span>
            </button>
          )}
        </div>

        {img && (
          <p className="-mt-1 text-[11px] text-cream-3">
            Drag the image to reposition · use the slider to zoom
          </p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) loadFile(f);
            e.target.value = '';
          }}
        />

        {img && (
          <>
            <div className="flex w-full items-center gap-3 rounded-lg bg-ink-3 p-2.5">
              <div className="flex items-center gap-2 text-[11px] text-cream-3">
                Preview
              </div>
              <div className="flex items-center gap-2">
                <CropPreview img={img} zoom={zoom} offset={offset} size={56} />
                <CropPreview img={img} zoom={zoom} offset={offset} size={32} />
                <CropPreview img={img} zoom={zoom} offset={offset} size={20} />
              </div>
            </div>
          <div className="flex w-full items-center gap-2">
            <label className="text-xs text-cream-3">Zoom</label>
            <input
              type="range"
              className="flex-1"
              min={0.1}
              max={4}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
            <button type="button" className="btn btn-ghost text-xs" onClick={reset}>
              Reset
            </button>
          </div>
          </>
        )}

        {error && <p className="text-xs text-rust">{error}</p>}

        <div className="flex w-full items-center justify-between">
          <div className="flex gap-2">
            {img && (
              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={() => setImg(null)}
              >
                Pick different image
              </button>
            )}
            {initialUrl && !img && (
              <UiNode
                as="button"
                name="avatar-editor-remove-button"
                entity="user_avatar"
                action="remove"
                type="button"
                className="btn btn-ghost text-xs text-rust"
                onClick={remove}
                disabled={busy}
              >
                Remove current
              </UiNode>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <UiNode
              as="button"
              name="avatar-editor-save-button"
              entity="user_avatar"
              action="upload"
              state={busy ? 'busy' : 'idle'}
              type="button"
              className="btn btn-primary inline-flex items-center gap-2"
              onClick={save}
              disabled={!img || busy}
            >
              {busy && <Spinner size={12} />}
              Save photo
            </UiNode>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Compute the source-image rectangle that the crop frame currently
 * displays, in image-natural coordinates. Pure / testable.
 *
 * At zoom `z`, one source pixel takes `z` screen pixels. The image's
 * centre sits at (frameSize/2 + offset) on screen; the visible frame
 * is at (0..frameSize). Map back: each frame pixel covers 1/z source
 * pixels, and the frame centre maps to source coord
 *   (naturalWidth/2 - offset.x/z, naturalHeight/2 - offset.y/z).
 */
export interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}
export function computeCropRect(
  naturalWidth: number,
  naturalHeight: number,
  zoom: number,
  offset: { x: number; y: number },
  frameSize = FRAME,
): CropRect {
  const sw = frameSize / zoom;
  const sx = naturalWidth / 2 - offset.x / zoom - sw / 2;
  const sy = naturalHeight / 2 - offset.y / zoom - sw / 2;
  return { sx, sy, sw, sh: sw };
}

/**
 * Crop the visible window of the live image (matching the on-screen
 * frame + transform) to a fixed OUTPUT_SIZE square and export as JPEG.
 */
export async function renderCrop(
  img: HTMLImageElement,
  zoom: number,
  offset: { x: number; y: number },
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unsupported');

  const r = computeCropRect(img.naturalWidth, img.naturalHeight, zoom, offset);
  ctx.drawImage(img, r.sx, r.sy, r.sw, r.sh, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas export failed'))),
      'image/jpeg',
      OUTPUT_QUALITY,
    ),
  );
}

/**
 * Live preview of the cropped avatar at a given display size. Driven
 * by the same zoom/offset math as renderCrop, but renders as a styled
 * <div> with the source image positioned to match the crop window —
 * no canvas, so it updates in real time without any drawing cost.
 */
function CropPreview({
  img,
  zoom,
  offset,
  size,
}: {
  img: HTMLImageElement;
  zoom: number;
  offset: { x: number; y: number };
  size: number;
}) {
  // Mirror the same math as renderCrop / computeCropRect, scaled to
  // the preview size. The source rect is FRAME/zoom wide and centred
  // on (img.naturalWidth/2 - offset.x/zoom, naturalHeight/2 - offset.y/zoom).
  const sw = FRAME / zoom;
  const sx = img.naturalWidth / 2 - offset.x / zoom - sw / 2;
  const sy = img.naturalHeight / 2 - offset.y / zoom - sw / 2;
  // Scale so the source square (sw) fills the preview circle (size).
  const scale = size / sw;
  const imgW = img.naturalWidth * scale;
  const imgH = img.naturalHeight * scale;
  const left = -sx * scale;
  const top = -sy * scale;
  return (
    <span
      className="relative inline-block overflow-hidden rounded-full"
      style={{
        width: size,
        height: size,
        background: 'var(--color-ink-2)',
      }}
      aria-hidden
    >
      <img
        src={img.src}
        alt=""
        draggable={false}
        style={{
          position: 'absolute',
          left,
          top,
          width: imgW,
          height: imgH,
          maxWidth: 'none',
        }}
      />
    </span>
  );
}
